import { activeProgramKeys, AFFECTED_PERSONS, askOptionValues, DATA_CLASSES, getProgramManifest, onboardingAskOptions, programHas, programsWhere, type ProgramKey } from '@aigovops/core';
import type { CoverageRegion } from './api';
import type { SystemIntakeValue } from '../components/SystemIntakeFields';
import { AI_PROVIDER_LABELS, providersFromIndicators as coreProvidersFromIndicators } from '@aigovops/core';

/** V98-3-6: programs offered at first-system time (ADR-0048 §1) — the active manifests, in order (ADR-0050). */
export type { ProgramKey };
export const PROGRAM_KEYS: readonly ProgramKey[] = Object.freeze(activeProgramKeys() as ProgramKey[]);
export type DecisionDomain = 'hiring' | 'care' | 'coverage' | 'credit' | 'none';
export type EuRole = '' | 'provider' | 'deployer' | 'both' | 'ask_legal';

export interface FirstSystemAnswers {
  /** Q1 — '' means "you" (the person onboarding). */
  ownerUserId: string;
  /** Q2 — empty + programsNone=false means unanswered. */
  programs: ProgramKey[];
  programsNone: boolean;
  soc2ReportType: 'type1' | 'type2';
  fedrampClass: 'A' | 'B' | 'C';
  /** V103-3-7: program options onboarding asks (manifest `onboarding.askOptions`, e.g. GovRAMP tier + impact level), per program. */
  programOptions: Partial<Record<string, Record<string, string>>>;
  /** Q3 — starts unselected and is required. */
  federalData: '' | 'yes' | 'no' | 'unsure';
  /** Confirmed facts ('' = not known yet). */
  internetReachable: '' | 'yes' | 'no';
  /** Undefined = take what the scan derived. */
  providers?: string[];
  /** Conditional (AI-law tracking on). */
  decidesAbout: DecisionDomain[];
  states: string[];
  euRole: EuRole;
  /** Found rows the person unticked as not correct. */
  disputed: string[];
  /** Fingerprint of the answers last saved to the workspace. */
  intakeSaved?: string;
}

export interface OnboardingDraft extends SystemIntakeValue, FirstSystemAnswers {
  person: string; company: string; role: string; kind: 'personal' | 'team' | 'consultant';
  clientDomain: string; clientEmail: string; pathway: 'solopreneur' | 'enterprise';
  systemId?: string; classification?: Record<string, unknown>; classifiedInput?: string;
  service: 'agentic_systems' | 'ai_agents' | 'llm_services' | 'ml_models' | 'data_pipelines';
  step: number;
  coverageRegions: CoverageRegion[];
  scanIdentity?: string; rejectedRefs?: string[];
}
export const initialOnboardingDraft: OnboardingDraft = {
  person: '', company: '', role: '', kind: 'team', clientDomain: '', clientEmail: '', pathway: 'solopreneur',
  name: '', purpose: '', autonomy: 'human_in_the_loop', markets: [], dataClasses: [], affectedPersons: [], consequential: false,
  service: 'llm_services', step: 1, coverageRegions: [],
  ownerUserId: '', programs: [], programsNone: false, soc2ReportType: 'type2', fedrampClass: 'B', programOptions: {}, federalData: '',
  internetReachable: '', decidesAbout: [], states: [], euRole: '', disputed: [],
};
export function intakeFingerprint(draft: SystemIntakeValue): string {
  return JSON.stringify([draft.name.trim(), draft.purpose.trim(), draft.autonomy, [...draft.markets].sort(), [...draft.dataClasses].sort(), [...draft.affectedPersons].sort(), draft.consequential]);
}
export function draftKey(userId: string, slug?: string, systemId?: string): string { return `fedsec:onboarding:v75:${userId}:${slug ?? 'new'}${systemId ? `:system:${systemId}` : ''}`; }
const ARRAY_FIELDS = ['markets', 'dataClasses', 'affectedPersons', 'coverageRegions', 'programs', 'decidesAbout', 'states', 'disputed'] as const;
export function readOnboardingDraft(storage: Pick<Storage, 'getItem'>, key: string): OnboardingDraft {
  try {
    const value = JSON.parse(storage.getItem(key) ?? '{}') as Partial<OnboardingDraft>;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return { ...initialOnboardingDraft };
    const draft = { ...initialOnboardingDraft, ...value };
    for (const field of ARRAY_FIELDS) if (!Array.isArray(draft[field]) || (draft[field] as unknown[]).some(v => typeof v !== 'string')) (draft as Record<string, unknown>)[field] = [];
    if (draft.providers !== undefined && (!Array.isArray(draft.providers) || draft.providers.some(v => typeof v !== 'string'))) draft.providers = undefined;
    draft.programs = draft.programs.filter(p => PROGRAM_KEYS.includes(p));
    if (!['', 'yes', 'no', 'unsure'].includes(draft.federalData)) draft.federalData = '';
    if (!draft.programOptions || typeof draft.programOptions !== 'object' || Array.isArray(draft.programOptions)) draft.programOptions = {};
    return draft;
  } catch { return { ...initialOnboardingDraft }; }
}

// ---- V98-3-6: first-system rules (pure; the page renders from these) ---------

/** Q2: "Neither" is mutually exclusive with every program. */
export function toggleProgram(a: Pick<FirstSystemAnswers, 'programs' | 'programsNone'>, key: ProgramKey | 'none', checked: boolean): Pick<FirstSystemAnswers, 'programs' | 'programsNone'> {
  if (key === 'none') return checked ? { programs: [], programsNone: true } : { programs: a.programs, programsNone: false };
  const programs = checked ? [...new Set([...a.programs, key])] : a.programs.filter(p => p !== key);
  return { programs: PROGRAM_KEYS.filter(p => programs.includes(p)), programsNone: checked ? false : a.programsNone };
}

const DOMAIN_PEOPLE: Record<Exclude<DecisionDomain, 'none'>, string> = { hiring: 'job_applicants', care: 'patients', coverage: 'borrowers', credit: 'borrowers' };

/**
 * "Does it help decide something about a person?" — "None" is exclusive; the
 * answer also sets the consequential flag and adds the people it implies.
 */
export function toggleDecision(d: Pick<OnboardingDraft, 'decidesAbout' | 'affectedPersons'>, key: DecisionDomain, checked: boolean): Pick<OnboardingDraft, 'decidesAbout' | 'affectedPersons' | 'consequential'> {
  let decidesAbout: DecisionDomain[];
  if (key === 'none') decidesAbout = checked ? ['none'] : [];
  else decidesAbout = checked ? [...new Set([...d.decidesAbout.filter(x => x !== 'none'), key])] : d.decidesAbout.filter(x => x !== key);
  const implied = decidesAbout.filter((x): x is Exclude<DecisionDomain, 'none'> => x !== 'none').map(x => DOMAIN_PEOPLE[x]);
  return { decidesAbout, affectedPersons: [...new Set([...d.affectedPersons, ...implied])], consequential: implied.length > 0 };
}

/** AI-law tracking is on when the program that holds legal determinations (AI Regulatory) covers the system. */
export const aiLawTracking = (a: Pick<FirstSystemAnswers, 'programs'>) => a.programs.some(p => programHas(p, m => m.features.legalDeterminations));
/** Conditional questions — shown only when their trigger is set. */
export function conditionalQuestions(d: Pick<OnboardingDraft, 'programs' | 'markets'>): { decides: boolean; states: boolean; euRole: boolean } {
  const tracking = aiLawTracking(d);
  return { decides: tracking, states: tracking && d.markets.includes('us'), euRole: tracking && d.markets.includes('eu') };
}
/** The program federal-data systems need (manifest `coverage.federalDataGaps`: FedRAMP), if any is active. */
export const FEDERAL_DATA_PROGRAM: ProgramKey | null = (programsWhere(m => m.coverage.federalDataGaps)[0]?.key ?? null) as ProgramKey | null;
/** Warn: federal data is Yes but FedRAMP isn't chosen. */
export const federalWithoutFedramp = (a: Pick<FirstSystemAnswers, 'federalData' | 'programs'>) =>
  a.federalData === 'yes' && !!FEDERAL_DATA_PROGRAM && !a.programs.some(p => programHas(p, m => m.coverage.federalDataGaps));

export interface FieldError { field: 'name' | 'purpose' | 'owner' | 'programs' | 'federal' | 'markets'; message: string }
export function validateFirstSystem(d: OnboardingDraft): FieldError[] {
  const errors: FieldError[] = [];
  if (!d.name.trim()) errors.push({ field: 'name', message: 'Enter a system name.' });
  if (!d.purpose.trim()) errors.push({ field: 'purpose', message: 'Describe the main use.' });
  if (!d.programs.length && !d.programsNone) errors.push({ field: 'programs', message: 'Choose the programs that cover it, or Neither.' });
  if (!d.federalData) errors.push({ field: 'federal', message: 'Answer whether it handles federal government data.' });
  if (!d.markets.length) errors.push({ field: 'markets', message: 'Set at least one market.' });
  return errors;
}

/** Markets derived from the regions chosen in step 3 (empty = nothing to derive). */
export function marketsFromRegions(regions: readonly CoverageRegion[]): string[] {
  const out = new Set<string>();
  for (const r of regions) out.add(r === 'US' ? 'us' : r === 'EU' ? 'eu' : 'global');
  return ['us', 'eu', 'global'].filter(m => out.has(m));
}

/** Model providers derived from the repo scan's dependency indicators ("anthropic in package.json"). */
// V108-3-4: one provider vocabulary, shared with discovery's derived facts (core). Adds Bedrock /
// SageMaker / Vertex / Azure OpenAI / Gemini / Mistral / Cohere and langchain provider packages.
export const PROVIDER_LABELS: Record<string, string> = { ...AI_PROVIDER_LABELS };
export const providersFromIndicators = (indicators: readonly string[]): string[] => coreProvidersFromIndicators(indicators);

/** The saved-answers fingerprint: re-save only when something changed. */
export function answersFingerprint(d: OnboardingDraft, providers: string[], ownerId: string): string {
  const c = conditionalQuestions(d);
  return JSON.stringify([d.systemId ?? '', ownerId, [...d.programs], d.programsNone, d.soc2ReportType, d.fedrampClass, d.programs.map((p) => programOptionsFor(d, p) ?? null), d.federalData, d.internetReachable,
    [...providers].sort(), c.decides ? [...d.decidesAbout].sort() : [], c.states ? [...d.states].sort() : [], c.euRole ? d.euRole : '', d.dataClasses.includes('phi')]);
}

export interface FirstSystemIntakeRequest {
  system_id: string;
  owner_user_id: string;
  /** One entry per program; carries the profile columns its manifest requires (report_type, certification_class). */
  programs: Array<{ program: ProgramKey; report_type?: 'type1' | 'type2'; certification_class?: 'A' | 'B' | 'C'; program_options?: Record<string, string> }>;
  federal_data: 'yes' | 'no' | 'unsure';
  internet_reachable?: boolean | null;
  providers: string[];
  law?: { decides_about?: DecisionDomain[]; states?: string[]; eu_role?: Exclude<EuRole, ''> | null; phi?: boolean };
}

/** The program's entry: the profile columns its manifest requires, from the draft's sub-answers. */
/**
 * V103-3-7: the options a program asks at onboarding (manifest `onboarding.askOptions`), each the
 * person's pick or the add-program default. Undefined when the program asks none.
 */
export function programOptionsFor(d: Pick<FirstSystemAnswers, 'programOptions'>, program: ProgramKey): Record<string, string> | undefined {
  const m = getProgramManifest(program);
  const ask = m ? onboardingAskOptions(m) : undefined;
  if (!m || !ask?.length) return undefined;
  const picked = d.programOptions?.[program] ?? {};
  // Asked in order; each option offers only the values the earlier picks allow
  // (V106-3-2: GovRAMP tiers per impact level): the pick, else the add-program default, else the first offered.
  const out: Record<string, string> = {};
  for (const o of ask) {
    const values = askOptionValues(m, o.key, o.values, out);
    const offered = (v: unknown) => values.some((x) => x.value === v);
    const def = m.onboarding.addProgramDefaults[o.key];
    out[o.key] = offered(picked[o.key]) ? String(picked[o.key]) : offered(def) ? String(def) : (values[0] ?? o.values[0]!).value;
  }
  return out;
}

/** V106-3-2: the values onboarding offers for one option, given the program's current picks. */
export function offeredOptionValues(d: Pick<FirstSystemAnswers, 'programOptions'>, program: ProgramKey, key: string): ReadonlyArray<{ value: string; label: string }> {
  const m = getProgramManifest(program);
  const o = m ? onboardingAskOptions(m).find((x) => x.key === key) : undefined;
  if (!m || !o) return [];
  return askOptionValues(m, key, o.values, programOptionsFor(d, program) ?? {});
}

function intakeProgram(d: Pick<FirstSystemAnswers, 'soc2ReportType' | 'fedrampClass' | 'programOptions'>, program: ProgramKey): FirstSystemIntakeRequest['programs'][number] {
  const cols = getProgramManifest(program)?.profile.columns ?? {};
  const options = programOptionsFor(d, program);
  return {
    program,
    ...(cols.report_type?.rule === 'required' ? { report_type: d.soc2ReportType } : {}),
    ...(cols.certification_class?.rule === 'required' ? { certification_class: d.fedrampClass } : {}),
    ...(options ? { program_options: options } : {}),
  };
}

/** The request body: only the questions that were shown are sent; law answers become a Legal proposal server-side. */
export function toIntakeRequest(d: OnboardingDraft, systemId: string, ownerId: string, providers: string[]): FirstSystemIntakeRequest {
  const c = conditionalQuestions(d);
  const law: NonNullable<FirstSystemIntakeRequest['law']> = {};
  if (c.decides && d.decidesAbout.length) law.decides_about = d.decidesAbout;
  if (c.states && d.states.length) law.states = d.states;
  if (c.euRole && d.euRole) law.eu_role = d.euRole;
  if (d.dataClasses.includes('phi')) law.phi = true;
  return {
    system_id: systemId, owner_user_id: ownerId,
    programs: d.programsNone ? [] : d.programs.map(p => intakeProgram(d, p)),
    federal_data: (d.federalData || 'unsure') as 'yes' | 'no' | 'unsure',
    ...(d.internetReachable ? { internet_reachable: d.internetReachable === 'yes' } : {}),
    providers,
    ...(Object.keys(law).length ? { law } : {}),
  };
}

/** What will be sent to Legal as a proposal (mirrors core `legalProposalLines` triggers). */
export function needsLegal(d: OnboardingDraft): boolean {
  const c = conditionalQuestions(d);
  return d.dataClasses.includes('phi') || d.federalData === 'unsure'
    || (c.decides && d.decidesAbout.length > 0) || (c.states && d.states.length > 0) || (c.euRole && !!d.euRole);
}

// V85-6-4 (gap E7): the client-side `firstRunYaml` builder that used to
// live here — requiring Sam to type a 40-character pinned action SHA and
// the deployed API origin by hand — is retired. The workflow is now
// generated server-side by POST /api/w/:slug/setup/gac-config, which
// derives the action reference from the platform-pinned GAC_ACTION_REF
// binding and the origin from the request itself. See
// packages/cloudflare/src/routes/setup.ts and
// packages/web/src/lib/api.ts (`generateGacConfig`).

/**
 * Wizard steps in order. Deep links use the step *name*, never a number, so
 * inserting a step (as v9.2 did with regions) cannot silently re-point links.
 */
export const ONBOARDING_STEPS = [
  { key: 'profile', title: 'Who are you?' },
  { key: 'path', title: 'Choose your path' },
  { key: 'regions', title: 'Which regions do you operate in?' },
  { key: 'connect', title: 'Connect your sources' },
  { key: 'findings', title: 'What we found' },
  { key: 'plan', title: 'Your plan' },
  { key: 'first_system', title: 'Your first system' },
  { key: 'first_run', title: 'What we can already see' },
] as const;
export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number]['key'];

/** 1-based step number for a named step. */
export function onboardingStepNumber(key: OnboardingStepKey): number {
  return ONBOARDING_STEPS.findIndex((s) => s.key === key) + 1;
}

/**
 * The step a `?step=` value names: a step key (`connect`, the form server redirects use since
 * V108-1-4) or a 1-based number (older links). Anything else is null.
 */
export function stepFromParam(raw: string | null): number | null {
  if (!raw) return null;
  const byKey = ONBOARDING_STEPS.findIndex((s) => s.key === raw);
  if (byKey >= 0) return byKey + 1;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= ONBOARDING_STEPS.length ? n : null;
}

/** Link to a named onboarding step, optionally scoped to a system. */
export function onboardingHref(slug: string, key: OnboardingStepKey, opts: { system?: string } = {}): string {
  const q = new URLSearchParams({ step: String(onboardingStepNumber(key)) });
  if (opts.system) q.set('system', opts.system);
  return `/w/${slug}/onboarding?${q.toString()}`;
}


/**
 * The intake form's options predate the use-case vocabulary the server validates
 * (POST /use-cases → 422 on any unknown value). Map onto that vocabulary: people
 * by nearest equivalent; data classes outside it (proprietary, public,
 * de_identified) carry no classification weight and are left out.
 */
// V112-2-9 (PD-2): borrowers and tenants are core values now, kept as themselves (no longer folded into consumers).
const USE_CASE_PEOPLE: Record<string, string> = { employees: 'workers' };
export function toUseCaseVocabulary(d: Pick<OnboardingDraft, 'dataClasses' | 'affectedPersons'>): { data_classes: string[]; affected_persons: string[] } {
  const dataOk = new Set<string>(DATA_CLASSES);
  const peopleOk = new Set<string>(AFFECTED_PERSONS);
  const people = d.affectedPersons.map((p) => USE_CASE_PEOPLE[p] ?? p).filter((p) => peopleOk.has(p));
  return { data_classes: d.dataClasses.filter((c) => dataOk.has(c)), affected_persons: [...new Set(people)] };
}
